/**
 * A stable id for THIS client instance, used to make calls multi-device safe.
 *
 * Scoped to the tab by construction — it lives only in module memory, so a
 * duplicated tab, a `window.open`, and a fresh window each evaluate this module
 * again and get their own id. That matters because a tab is what actually holds
 * a media session: there is exactly one call dock per tab, and two tabs on one
 * laptop have the same handoff problem as a laptop and a phone.
 *
 * Deliberately NOT persisted. `sessionStorage` looks like the natural home, but
 * browsers *copy* it into a duplicated tab, so persisting would hand two live
 * tabs the same id — reintroducing exactly what device ids exist to prevent —
 * and would then need a cross-tab handshake to repair it. Nothing needs the id
 * to survive a reload: the call slice isn't persisted (see `partialize`), so a
 * reloaded tab holds no seat to reclaim, and rejoining claims a new one.
 *
 * The server echoes this value back inside `call.ring_resolved`, which is how a
 * device tells "I answered" from "another of my devices answered" without the
 * server ever having to map sockets to devices.
 *
 * Charset note: the id is stored server-side and validated against
 * `[A-Za-z0-9_-]{1,64}`. `randomUUID()` satisfies that, and so do the fallbacks.
 */

let deviceId: string | null = null

function randomId(): string {
  const globalCrypto = globalThis.crypto as Crypto | undefined
  if (globalCrypto?.randomUUID) return globalCrypto.randomUUID()

  if (globalCrypto?.getRandomValues) {
    const bytes = globalCrypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }

  // Last resort. Only reachable on a platform with no Web Crypto at all; the id
  // needs to be unique, not unguessable — it authorizes nothing on its own.
  return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}

export function getDeviceId(): string {
  deviceId ??= randomId()
  return deviceId
}
